import { Component, Input } from '@angular/core';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    DO_NOTHING,
    InviteDto,
    OrganisationIdentifierDto,
    OrganisationService,
    PersonDto,
    PreferencesService,
    ProfileService,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import {
    DashboardSelectionOptions,
    dashboardSelectionDisplayableLabels
} from '@flyfreely-portal-ui/ui';
import { getOrElse } from 'fp-ts/es6/Option';
import { firstValueFrom, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { basicUserProfileFields } from '../fieldsets';
import { ProfileDialoguesService } from '../profile-dialogues.service';

const PROFILE_OPTIONS_COMPONENT = 'profile-options';
const DEFAULT_DASHBOARD = 'DEFAULT';
@Component({
    selector: 'profile-details',
    templateUrl: './profile-details.component.html'
})
export class ProfileDetailsComponent {
    @Input() currentUser: PersonDto;
    @Input() invites: InviteDto[];
    @Input() canUseNotams: boolean;

    userAsaIdentifiers: OrganisationIdentifierDto;

    dashboardOptions: { value: string; label: string }[] = [
        { value: DEFAULT_DASHBOARD, label: 'Organisation Default' },
        ...Object.values(DashboardSelectionOptions).map(v => ({
            value: v,
            label: dashboardSelectionDisplayableLabels[v]
        }))
    ];
    selectedDefaultDashboard: string = 'DEFAULT';

    private ngUnsubscribe$ = new Subject<void>();
    private workTracker = new WorkTracker();
    working: boolean;

    constructor(
        private profileDialoguesService: ProfileDialoguesService,
        private organisationService: OrganisationService,
        private commonDialoguesService: CommonDialoguesService,
        private profileService: ProfileService,
        private preferencesService: PreferencesService
    ) {}

    ngOnInit() {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));

        this.refreshPreferences();

        this.refreshAsaDetails();
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    refreshPreferences() {
        this.preferencesService
            .findPreferencesAsOption<{ defaultDashboard?: string }>(
                PROFILE_OPTIONS_COMPONENT,
                null
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(option => {
                const prefs = getOrElse(
                    () => ({} as { defaultDashboard?: string })
                )(option);
                this.selectedDefaultDashboard =
                    prefs.defaultDashboard ?? DEFAULT_DASHBOARD;
            });
    }

    refreshAsaDetails() {
        if (!this.canUseNotams) {
            return;
        }
        this.organisationService
            .findOrganisationIdentifiers(
                this.currentUser.personalOrganisationId,
                OrganisationIdentifierDto.Integration.ASA
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: result => {
                    this.userAsaIdentifiers = result;
                },
                error: () => {
                    this.userAsaIdentifiers = {
                        identifier: null,
                        integration: null
                    };
                }
            })
            .add(this.workTracker.createTracker());
    }

    manageAccountDetails() {
        window.open(
            'https://dev-id.flyfreely.io/auth/realms/FlyFreely/account',
            '_blank'
        );
    }

    updatePersonalDetails() {
        this.commonDialoguesService
            .showFormlyDialogue(
                'Update Personal Details',
                'Update',
                true,
                true,
                basicUserProfileFields(),
                {
                    firstName: this.currentUser.firstName,
                    lastName: this.currentUser.lastName,
                    phoneNumber: this.currentUser.phoneNumber
                },
                data =>
                    this.profileService
                        .updateDetails({
                            ...data
                        })
                        .toPromise()
            )
            .catch(DO_NOTHING);
    }

    get selectedDefaultDashboardLabel() {
        return this.dashboardOptions.find(
            option => option.value === this.selectedDefaultDashboard
        )?.label;
    }

    // Done as an arrow function so it can be used as a callback
    updateDefaultDashboard = (value: { defaultDashboard: string }) => {
        const selected = value?.defaultDashboard ?? DEFAULT_DASHBOARD;
        const defaultDashboard =
            selected === DEFAULT_DASHBOARD ? undefined : selected;
        return firstValueFrom(
            this.preferencesService
                .updatePreferences(PROFILE_OPTIONS_COMPONENT, null, {
                    defaultDashboard
                })
                .pipe(takeUntil(this.ngUnsubscribe$))
        ).then(
            () => {
                this.selectedDefaultDashboard = selected;
            },
            () => Promise.reject('Could not update the default dashboard')
        );
    };

    updateAirServices() {
        const dialogue = this.profileDialoguesService.showAirServicesDialogue(
            this.currentUser,
            this.userAsaIdentifiers
        );

        dialogue.onHide.pipe(take(1)).subscribe(() => this.refreshAsaDetails());
    }
}
